import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
  Alert,
  TextField,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import SportsTennisRoundedIcon from "@mui/icons-material/SportsTennisRounded";
import PaymentIcon from "@mui/icons-material/Payment";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";

import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { payments } from "@square/web-sdk";
import { db, functions } from "../firebase";

// ---------- Utility Functions ----------
function getDurationUnit(cadence) {
  switch (cadence) {
    case "DAILY":
      return "Days";
    case "WEEKLY":
      return "Weeks";
    case "MONTHLY":
      return "Months";
    case "ANNUAL":
      return "Year";
    case "NINETY_DAYS":
      return "Season";
    default:
      return "";
  }
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
}

function toLocalDateInput(val) {
  if (!val) return "";
  const d = new Date(val);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const CADENCE_LABELS = {
  NINETY_DAYS: "One Payment",
  DAILY:        "Daily Payments",
  WEEKLY:       "Weekly Payments",
  MONTHLY:      "Monthly Payments",
  ANNUAL:       "One Payment",
};

function formatPhase(phase) {
  // pick up our human-friendly label (fall back to splitting _)
  const raw = phase.cadence;
  let label = CADENCE_LABELS[raw];
  if (!label) {
    label = raw
      .toLowerCase()
      .split("_")
      .map(w => w[0].toUpperCase() + w.slice(1))
      .join(" ");
  }

  // now build the "<count> <Label> Payment(s)" string
  const count = phase.periods ?? 0;
  const paymentWord = count === 1 ? "Payment" : "Payments";
  return `${count} ${label} ${paymentWord}`;
}



function extractPlanDetails(variation) {
  const phase = variation?.subscription_plan_variation_data?.phases?.[0] || {};
  const price = Number(phase?.pricing?.price?.amount || 0);
  const currency = phase?.pricing?.price?.currency || "AUD";
  const durationNum = Number(phase?.periods) || 0;
  const cadence = phase?.cadence || "";

  const rawName = variation?.subscription_plan_variation_data?.name || "";
  const cleanName = rawName.replace("Variation", "").trim();

  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(price / 100);

  const durationUnit = getDurationUnit(cadence);
  const durationLabel = durationNum > 0 ? `${durationNum} ${durationUnit}` : "Forever";

  let planType = "Other";
  if (cleanName.toLowerCase().includes("annual")) planType = "Annual";
  if (cleanName.toLowerCase().includes("season")) planType = "Seasonal";

  let planCategory = "Adult";
  if (cleanName.toLowerCase().includes("junior")) planCategory = "Junior";

  const features = {
    "24/7 Access": cleanName.includes("24/7"),
    "Unlimited Pennants Per Season": cleanName.includes("Multi"),
    "One Pennant Per Season": cleanName.includes("Single"),
    "Gym Access": cleanName.includes("Gym"),
  };

  return {
    variationId: variation.id,
    name: cleanName || "N/A",
    price: formattedPrice,
    rawPrice: price,
    paymentFrequency: cadence,
    durationNum,
    duration: durationLabel,
    planType,
    planCategory,
    features,
  };
}

function sortVariationsByCadence(variations) {
  const orderMap = { WEEKLY: 1, MONTHLY: 2, NINETY_DAYS: 3, ANNUAL: 4 };
  return variations.slice().sort((a, b) => {
    const orderA = orderMap[a.paymentFrequency] ?? 99;
    const orderB = orderMap[b.paymentFrequency] ?? 99;
    return orderA - orderB;
  });
}

// ---------- Main Component ----------
function MembershipManager({ squareCustomerId }) {
  const [allPlans, setAllPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planTypeFilter, setPlanTypeFilter] = useState("Annual");
  const [planCategoryFilter, setPlanCategoryFilter] = useState("Adult");
  const [availableCards, setAvailableCards] = useState([]);
  const [cardSelectionDialogOpen, setCardSelectionDialogOpen] = useState(false);
  const [loadingPaymentDetailsFor, setLoadingPaymentDetailsFor] = useState(null);
  const [membershipExpiry, setMembershipExpiry] = useState("");

  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);

  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [card, setCard] = useState(null);
  const [isCardInitialized, setIsCardInitialized] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    cardholderName: "",
    addressLine1: "",
    addressLine2: "",
    locality: "",
    administrativeDistrictLevel1: "",
    postalCode: "",
    country: "AU",
  });

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionDate, setActionDate] = useState("");
  const [actionSubscriptionId, setActionSubscriptionId] = useState("");

  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // ------------------------------------------------
  //  Subscriptions: Realtime with onSnapshot
  // ------------------------------------------------
  useEffect(() => {
    if (!squareCustomerId) return;
    setLoadingSubs(true);

    const unsubscribe = onSnapshot(
      doc(db, "members", squareCustomerId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSubscriptions(data.subscriptions || []);

          if (data.membershipExpiry) {
            // use explicit expiry if set
            setMembershipExpiry(toLocalDateInput(data.membershipExpiry));
          } else if (data.subscriptions?.length) {
            // otherwise pick the latest endDate from subscriptions
            const latest = data.subscriptions
              .map((s) => s.endDate)
              .filter(Boolean)
              .sort((a, b) => new Date(b) - new Date(a))[0];
            setMembershipExpiry(toLocalDateInput(latest));
          } else {
            // no subscriptions and no manual expiry
            setMembershipExpiry("");
          }
        }
        setLoadingSubs(false);
      }
    );

    return () => unsubscribe();
  }, [squareCustomerId]);



  const handleExpiryChange = async (e) => {
    const newDate = e.target.value;
    setMembershipExpiry(newDate);
    try {
      await updateDoc(doc(db, "members", squareCustomerId), {
        membershipExpiry: newDate
      });
      showSnackbar("Membership expiry updated.", "success");
    } catch (err) {
      console.error("Error updating expiry:", err);
      showSnackbar("Failed to update expiry.", "error");
    }
  };

  // ------------------------------------------------
  //  Fetch Plans
  // ------------------------------------------------
  useEffect(() => {
    if (!squareCustomerId) return;
    fetchAllPlans();
  }, [squareCustomerId]);

  async function fetchAllPlans() {
    setLoadingPlans(true);
    try {
      const fetchPlansFn = httpsCallable(functions, "fetchSquarePlans");
      const { data } = await fetchPlansFn();
      const flattened = (data.plans || [])
        .filter((plan) => !plan.is_deleted && plan.present_at_all_locations)
        .flatMap((plan) =>
          (plan.subscription_plan_data?.subscription_plan_variations || [])
            .filter((variation) => !variation.is_deleted)
            .map((variation) => extractPlanDetails(variation))
        );
      setAllPlans(flattened);
    } catch (error) {
      console.error("Error fetching plans:", error.message);
      showSnackbar("Failed to fetch plans.", "error");
    } finally {
      setLoadingPlans(false);
    }
  }

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ------------------------------------------------
  //  Initialize Square card
  // ------------------------------------------------
  useEffect(() => {
    if (showPaymentDialog && !isCardInitialized) {
      initializeSquareCard();
    }
  }, [showPaymentDialog, isCardInitialized]);

  async function initializeSquareCard() {
    try {
      const paymentsInstance = await payments(
        "sq0idp-vssGW3fD2LCsWJgBa4U6tg",
        "L7CGQXSP8NS44"
      );
      const cardInstance = await paymentsInstance.card();
      await cardInstance.attach("#card-container");
      setCard(cardInstance);
      setIsCardInitialized(true);
    } catch (err) {
      console.error("Error initializing Square card:", err);
    }
  }

  // ------------------------------------------------
  //  Payment Dialog
  // ------------------------------------------------
  const handleOpenPaymentDialog = async (subscriptionId) => {
    setLoadingPaymentDetailsFor(subscriptionId);
    try {
      const listCardsFn = httpsCallable(functions, "listCustomerCards");
      const { data } = await listCardsFn({ customerId: squareCustomerId });
      setSelectedSubscriptionId(subscriptionId);
      setAvailableCards(data.cards || []);
      setCardSelectionDialogOpen(true);
    } catch (err) {
      console.error("Error fetching cards:", err);
      setSelectedSubscriptionId(subscriptionId);
      setAvailableCards([]);
      setCardSelectionDialogOpen(true);
    } finally {
      setLoadingPaymentDetailsFor(null);
    }
  };

  const handleClosePaymentDialog = () => {
    setShowPaymentDialog(false);
    setBillingDetails({
      cardholderName: "",
      addressLine1: "",
      addressLine2: "",
      locality: "",
      administrativeDistrictLevel1: "",
      postalCode: "",
      country: "AU",
    });
  };

  const handleUpdatePaymentMethod = async () => {
    if (!card) {
      alert("Card form not loaded. Please try again.");
      return;
    }
    try {
      const result = await card.tokenize();
      if (result.status === "OK") {
        const updatePaymentFn = httpsCallable(functions, "updatePaymentMethod");
        await updatePaymentFn({
          customerId: squareCustomerId,
          sourceId: result.token,
          cardDetails: billingDetails,
        });
        showSnackbar("Payment method updated successfully.", "success");
        handleClosePaymentDialog();
      } else {
        alert("Failed to tokenize card. Please check your details.");
      }
    } catch (err) {
      console.error("Error updating payment method:", err.message);
      alert("Failed to update payment method. Please try again.");
    }
  };

  // ------------------------------------------------
  //  Refresh Subscriptions
  // ------------------------------------------------
  const handleRefreshSubscriptions = async () => {
    setGlobalLoading(true);
    try {
      const fetchCustomerFn = httpsCallable(functions, "fetchSquareCustomer");
      await fetchCustomerFn({ customerId: squareCustomerId });
      showSnackbar("Plan assigned.", "success");
    } catch (error) {
      console.error("Error refreshing plans:", error.message);
      showSnackbar("Failed to refresh plans.", "error");
    } finally {
      setGlobalLoading(false);
    }
  };

  // ------------------------------------------------
  //  Actions: Pause, Resume, Cancel
  // ------------------------------------------------
  const openActionDialog = (type, subscriptionId) => {
    setActionType(type);
    setActionSubscriptionId(subscriptionId);
    setActionDialogOpen(true);
  };
  const closeActionDialog = () => {
    setActionType("");
    setActionSubscriptionId("");
    setActionDate("");
    setActionDialogOpen(false);
  };

  const handleActionConfirm = async () => {
    setLoading(true);
    try {
      let actionFn;
      switch (actionType) {
        case "pause":
          actionFn = httpsCallable(functions, "pauseSubscription");
          break;
        case "resume":
          actionFn = httpsCallable(functions, "resumeSubscription");
          break;
        case "cancel":
          actionFn = httpsCallable(functions, "cancelSubscription");
          break;
        default:
          throw new Error("Invalid action type");
      }
      await actionFn({
        subscriptionId: actionSubscriptionId,
        date: actionDate || null,
        changeTiming: "IMMEDIATE",
      });
      showSnackbar(`${actionType} successful.`, "success");
      closeActionDialog();
      await handleRefreshSubscriptions();
    } catch (err) {
      console.error(`Error performing ${actionType}:`, err.message);
      showSnackbar(`Failed to perform ${actionType}.`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    setLoading(true);
    try {
      const cancelFn = httpsCallable(functions, "cancelSubscription");
      await cancelFn({ subscriptionId });
      showSnackbar("Membership canceled successfully.", "success");
      await handleRefreshSubscriptions();
    } catch (err) {
      showSnackbar("Failed to cancel membership. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------
  //  Delete Action
  // ------------------------------------------------
  const deleteAction = async (subscriptionId, actionId) => {
    setLoading(true);
    try {
      const deleteActionFn = httpsCallable(functions, "deleteSubscriptionAction");
      await deleteActionFn({ subscriptionId, actionId });
      showSnackbar("Action deleted successfully.");
      await handleRefreshSubscriptions();
    } catch (err) {
      console.error("Error deleting action:", err.message);
      showSnackbar("Failed to delete the action.", "error");
    } finally {
      setLoading(false);
    }
  };

  function renderActions(actions, subscriptionId) {
    if (!actions || actions.length === 0) return null;
    return (
      <List dense>
        {actions.map((action) => (
          <ListItem key={action.id}>
            <ListItemText
              primary={`Action: ${action.type}`}
              secondary={`Effective Date: ${formatDate(action.effectiveDate)}`}
            />
            <Tooltip title="Delete Action">
              <IconButton color="error" onClick={() => deleteAction(subscriptionId, action.id)}>
                <StopIcon />
              </IconButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    );
  }

  // ------------------------------------------------
  //  Assign Plan
  // ------------------------------------------------
  const handleAssignPlan = async (variationId) => {
    const blockingSub = subscriptions.find((sub) => {
      const hasCancelAction = sub.actions?.some((a) => a.type === "CANCEL");
      return (sub.status === "ACTIVE" || sub.status === "PAUSED") && !sub.canceledDate && !hasCancelAction;
    });
    if (blockingSub) {
      showSnackbar(
        `Already have an active/paused plan: ${blockingSub.planName}. Please cancel or wait until it ends.`,
        "error"
      );
      return;
    }
    try {
      setAssigning(true);
      const assignFn = httpsCallable(functions, "assignSquarePlan");
      await assignFn({ squareCustomerId, planVariationId: variationId });
      showSnackbar("Plan assigned successfully.", "success");
      await handleRefreshSubscriptions();
    } catch (error) {
      console.error("Error assigning plan:", error.message);
      showSnackbar("Failed to assign plan. Please try again.", "error");
    } finally {
      setAssigning(false);
    }
  };

  // ------------------------------------------------
  //  Visible Subscriptions
  // ------------------------------------------------
  const visibleSubscriptions = subscriptions.filter((sub) => {
    const hasCancelAction = sub.actions?.some((a) => a.type === "CANCEL");
    // Exclude subscriptions with a canceledDate OR with a status of "CANCELLED"
    return (
      (sub.status === "ACTIVE" || sub.status === "PAUSED") &&
      !sub.canceledDate &&
      sub.status !== "CANCELLED" &&
      !hasCancelAction
    );
  });
  const hasBlockingSubscription = visibleSubscriptions.length > 0;

  // ------------------------------------------------
  //  *NEW* Plan Filtering with "Sponsor" Tab
  // ------------------------------------------------
  function filterPlans(plans) {
    // If planTypeFilter is "Sponsor", show only plans with "sponsor" OR "life" in name
    if (planTypeFilter === "Sponsor") {
      return plans.filter((plan) => {
        const lowerName = plan.name.toLowerCase();
        return lowerName.includes("sponsor") || lowerName.includes("life");
      });
    }

    // Otherwise use the existing logic for type and category
    return plans.filter((plan) => {
      // Type logic
      const typeMatches =
        planTypeFilter === "Other"
          ? plan.planType !== "Annual" && plan.planType !== "Seasonal"
          : plan.planType === planTypeFilter;
      // Category logic
      const catMatches = plan.planCategory === planCategoryFilter;
      const isNotSponsor = !plan.name.toLowerCase().includes("sponsor") && !plan.name.toLowerCase().includes("life");
      return typeMatches && catMatches && isNotSponsor;
    });
  }

  function groupPlansByName(plansArray) {
    const grouped = {};
    plansArray.forEach((p) => {
      if (!grouped[p.name]) grouped[p.name] = [];
      grouped[p.name].push(p);
    });
    return grouped;
  }

  function sortGroupedPlansByUnlimited(groupedPlans) {
    const entries = Object.entries(groupedPlans);
    entries.sort(([_, variationsA], [__, variationsB]) => {
      const aIsUnlimited = variationsA.some((v) => v.name.toLowerCase().includes("multi"));
      const bIsUnlimited = variationsB.some((v) => v.name.toLowerCase().includes("multi"));
      if (aIsUnlimited && !bIsUnlimited) return -1;
      if (!aIsUnlimited && bIsUnlimited) return 1;
      return 0;
    });
    return entries;
  }

  function renderPlanSelectionUI() {
    const filtered = filterPlans(allPlans);
    if (filtered.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No membership plans available for this selection.
        </Typography>
      );
    }
    const grouped = groupPlansByName(filtered);
    const sortedGroups = sortGroupedPlansByUnlimited(grouped);

    return (
      <Grid container spacing={3}>
        {sortedGroups.map(([planName, planVars]) => {
          const sortedVars = sortVariationsByCadence(planVars);
          return (
            <Grid item xs={12} md={6} lg={4} key={planName}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {planName}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                    Includes:
                  </Typography>
                  <List dense disablePadding>
                    {Object.entries(sortedVars[0].features).map(([feature, included]) => {
                      if (!included) return null;
                      return (
                        <ListItem key={feature} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <SportsTennisRoundedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={feature} />
                        </ListItem>
                      );
                    })}
                  </List>
                  <Divider sx={{ my: 2 }} />
                  {sortedVars.map((v, idx) => {
                    const totalCost = (v.rawPrice * v.durationNum) / 100;
                    return (
                      <Box
                        key={v.variationId}
                        sx={{
                          pb: 2,
                          borderBottom: idx < sortedVars.length - 1 ? "1px solid #eee" : "none",
                          mb: idx < sortedVars.length - 1 ? 2 : 0,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {(CADENCE_LABELS[v.paymentFrequency] || v.paymentFrequency)}:{" "}
                          <Box component="span" sx={{ color: "primary.main", fontWeight: "bold" }}>
                            {v.price}
                          </Box>
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Duration: {v.durationNum > 0 ? v.duration : "Forever"}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Total Cost:{" "}
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "AUD",
                            }).format(totalCost)}
                          </Box>
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                          disabled={assigning}
                          onClick={() => handleAssignPlan(v.variationId)}
                        >
                          {assigning ? <CircularProgress size={18} /> : "Select"}
                        </Button>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  return (
    <Box sx={{ p: 3, position: "relative" }}>
      {globalLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#fff" }} />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Current Memberships (Staff View)
        </Typography>
        <Box sx={{ mb: 3, maxWidth: 240 }}>
          <Typography variant="subtitle2" gutterBottom>
            Membership Expiry
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={membershipExpiry ? toLocalDateInput(membershipExpiry) : ""}
            onChange={handleExpiryChange}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshSubscriptions}>
          Refresh Data
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Active/Pending Subscriptions */}
          <Grid container spacing={3}>
            {visibleSubscriptions.length ? (
              visibleSubscriptions.map((sub) => (
                <Grid item xs={12} md={6} lg={4} key={sub.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">
                        {sub.planName || "Unknown Plan"}
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="Status" secondary={sub.status || "N/A"} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Start Date" secondary={formatDate(sub.startDate)} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Paid Until" secondary={formatDate(sub.chargedThroughDate)} />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Plan End Date"
                            secondary={sub.endDate ? formatDate(sub.endDate) : "N/A"}
                          />
                        </ListItem>
                        {sub.phases?.length > 0 && (
  <>
    <Divider sx={{ my: 1 }} />
    <Typography variant="subtitle2" gutterBottom>
      Total Plan Payments
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
  {sub.phases.map((phase) => (
    <Box
      key={phase.uid}
      sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
    >
      <Typography variant="body2">
        {formatPhase(phase)}
      </Typography>
      <Typography variant="body2">
        {sub.paidInvoiceCount} of {phase.periods} total payments paid
      </Typography>
    </Box>
  ))}
</Box>

  </>
)}


                        {sub.card && sub.card.last4 && (
                          <ListItem>
                            <ListItemText
                              primary="Saved Card"
                              secondary={`${sub.card.brand} •••• ${sub.card.last4} (Exp: ${sub.card.expMonth}/${sub.card.expYear})`}
                            />
                          </ListItem>
                        )}
                        {sub.canceledDate && (
                          <ListItem>
                            <ListItemText
                              primary="Cancellation Date"
                              secondary={formatDate(sub.canceledDate)}
                            />
                          </ListItem>
                        )}
                      </List>
                      {sub.lastInvoice && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle1" gutterBottom>
                            Last Invoice
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemText primary="Invoice Status" secondary={sub.lastInvoice.status || "N/A"} />
                            </ListItem>
                            <ListItem>
                              <ListItemText
                                primary="Invoice Due Date"
                                secondary={sub.lastInvoice.dueDate ? formatDate(sub.lastInvoice.dueDate) : "N/A"}
                              />
                            </ListItem>
                          </List>
                        </>
                      )}
                      <Typography variant="subtitle1" sx={{ mt: 2 }}>
                        Pending Actions
                      </Typography>
                      {renderActions(sub.actions, sub.id) || "No pending actions."}
                    </CardContent>
                    <CardActions sx={{ justifyContent: "space-between" }}>
                      <Tooltip title="Update Payment Method">
                        <IconButton
                          onClick={() => handleOpenPaymentDialog(sub.id)}
                          color="primary"
                          disabled={loadingPaymentDetailsFor === sub.id}
                        >
                          {loadingPaymentDetailsFor === sub.id
                            ? <CircularProgress size={24} />
                            : <PaymentIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Pause Membership">
                        <IconButton onClick={() => openActionDialog("pause", sub.id)} color="warning" disabled={sub.status === "PAUSED"}>
                          <PauseIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Resume Membership">
                        <IconButton
                          onClick={() => openActionDialog("resume", sub.id)}
                          color="success"
                          disabled={
                            !(
                              sub.status === "PAUSED" ||
                              (sub.actions && sub.actions.some((action) => action.type === "PAUSE"))
                            )
                          }
                        >
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel Membership">
                        <IconButton onClick={() => handleCancelSubscription(sub.id)} color="error">
                          <StopIcon />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            ) : (
              <Box sx={{ p: 3, textAlign: "center", width: "100%" }}>
                <Typography variant="body2" color="textSecondary">
                  No current memberships or all are pending cancellation.
                </Typography>
              </Box>
            )}
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* If no blocking subscription, show plan selection */}
          {hasBlockingSubscription ? (
            <Typography variant="h6" gutterBottom>
              Cannot assign a new plan while an active or paused plan exists.
            </Typography>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Assign a New Membership Plan
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Assigning a plan will result in an invoice being emailed to the member.
              </Typography>
              {/* 
                 Add a new Sponsor tab for any plan that has "sponsor" or "life" 
                 in the name:
              */}
              <ToggleButtonGroup
                value={planTypeFilter}
                exclusive
                onChange={(e, newVal) => newVal && setPlanTypeFilter(newVal)}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="Annual">Annual</ToggleButton>
                <ToggleButton value="Seasonal">Seasonal</ToggleButton>
                <ToggleButton value="Other">Other</ToggleButton>
                <ToggleButton value="Sponsor">Sponsor/Life</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                value={planCategoryFilter}
                exclusive
                onChange={(e, newVal) => newVal && setPlanCategoryFilter(newVal)}
                sx={{ mb: 2, ml: 2 }}
              >
                <ToggleButton value="Adult">Adult</ToggleButton>
                <ToggleButton value="Junior">Junior</ToggleButton>
              </ToggleButtonGroup>
              {loadingPlans ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderPlanSelectionUI()
              )}
            </>
          )}
        </>
      )}

      {/* Payment Method Update Dialog */}
      <Dialog open={showPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Payment Method</DialogTitle>
        <DialogContent>
          <div id="card-container" style={{ marginBottom: "1rem" }} />
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Enter your payment details securely.
          </Typography>
          <TextField
            label="Cardholder Name"
            fullWidth
            margin="normal"
            value={billingDetails.cardholderName}
            onChange={(e) =>
              setBillingDetails((prev) => ({ ...prev, cardholderName: e.target.value }))
            }
          />
          <TextField
            label="Address Line 1"
            fullWidth
            margin="normal"
            value={billingDetails.addressLine1}
            onChange={(e) =>
              setBillingDetails((prev) => ({ ...prev, addressLine1: e.target.value }))
            }
          />
          <TextField
            label="Address Line 2"
            fullWidth
            margin="normal"
            value={billingDetails.addressLine2}
            onChange={(e) =>
              setBillingDetails((prev) => ({ ...prev, addressLine2: e.target.value }))
            }
          />
          <TextField
            label="City"
            fullWidth
            margin="normal"
            value={billingDetails.locality}
            onChange={(e) =>
              setBillingDetails((prev) => ({ ...prev, locality: e.target.value }))
            }
          />
          <TextField
            label="State"
            fullWidth
            margin="normal"
            value={billingDetails.administrativeDistrictLevel1}
            onChange={(e) =>
              setBillingDetails((prev) => ({ ...prev, administrativeDistrictLevel1: e.target.value }))
            }
          />
          <TextField
            label="Postal Code"
            fullWidth
            margin="normal"
            value={billingDetails.postalCode}
            onChange={(e) =>
              setBillingDetails((prev) => ({ ...prev, postalCode: e.target.value }))
            }
          />
          <TextField
            label="Country"
            fullWidth
            margin="normal"
            value={billingDetails.country}
            onChange={(e) =>
              setBillingDetails((prev) => ({ ...prev, country: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleUpdatePaymentMethod}>
            Update Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog (pause/resume/cancel) */}
      <Dialog open={actionDialogOpen} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType.charAt(0).toUpperCase() + actionType.slice(1)} Membership
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please select the effective date for this action.
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={actionDate}
            onChange={(e) => setActionDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleActionConfirm} disabled={!actionDate}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cardSelectionDialogOpen} onClose={() => setCardSelectionDialogOpen(false)}>
        <DialogTitle>Select Payment Method</DialogTitle>
        <DialogContent>
          {availableCards.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No saved payment methods.
            </Typography>
          )}
          {availableCards.map((card) => (
            <Box key={card.id} sx={{ mb: 2, p: 2, border: "1px solid #ccc", borderRadius: 1 }}>
              <Typography>{card.card_brand} ending in {card.last_4}</Typography>
              <Typography>Expires {card.exp_month}/{card.exp_year}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    const assignCardFn = httpsCallable(functions, "assignCardToSubscription");
                    console.log("Subscription ID:", selectedSubscriptionId);
                    try {
                      await assignCardFn({
                        subscriptionId: selectedSubscriptionId,
                        cardId: card.id,
                      });
                      showSnackbar("Payment method updated for subscription.");
                    } catch (err) {
                      console.error("Failed to assign card:", err);
                      showSnackbar("Failed to update payment method.", "error");
                    }
                    setCardSelectionDialogOpen(false);
                  }}
                >
                  Use this card
                </Button>

                <Button
                  variant="text"
                  color="error"
                  onClick={async () => {
                    const disableCardFn = httpsCallable(functions, "disableCard");
                    await disableCardFn({ cardId: card.id });
                    showSnackbar("Card removed.");
                    // Refresh list
                    const listCardsFn = httpsCallable(functions, "listCustomerCards");
                    const { data } = await listCardsFn({ customerId: squareCustomerId });
                    setAvailableCards(data.cards || []);
                  }}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
          <Divider sx={{ my: 2 }} />
          <Button
            variant="text"
            onClick={() => {
              setCardSelectionDialogOpen(false);
              setShowPaymentDialog(true);
            }}
          >
            Add new payment method
          </Button>
        </DialogContent>
      </Dialog>

    </Box>
  );
}

MembershipManager.propTypes = {
  squareCustomerId: PropTypes.string.isRequired,
};

export default MembershipManager;
